// src/utils/imageUtils.ts
import { BlogPostMetadata } from '../data/blogData';

// Default images to use as fallbacks
const DEFAULT_IMAGES = [
  '/images/blog-fallback-1.jpg',
  '/images/blog-fallback-2.jpg',
  '/images/blog-fallback-3.jpg',
  '/images/default-blog-image.jpg'
];

// Keep track of which images are assigned to which posts to avoid duplicates
const imageAssignmentMap = new Map<string, string>();
// Keep track of which image URLs we've already fetched from Unsplash
const fetchedImageUrls = new Set<string>();

// Function to generate a relevant image URL based on blog post metadata
export async function generateRelevantImage(post: BlogPostMetadata): Promise<string> {
  try {
    // If a custom image is already specified and not the default, use that
    if (post.image && 
        post.image !== '/images/default-blog-image.jpg' && 
        !post.image.includes('new blog post.svg') &&
        post.image !== '') {
      console.log(`Using existing image for "${post.title}": ${post.image}`);
      return post.image;
    }

    // Check if this post already has an assigned image in our map
    if (imageAssignmentMap.has(post.slug)) {
      const assignedImage = imageAssignmentMap.get(post.slug)!;
      console.log(`Using previously assigned image for "${post.title}": ${assignedImage}`);
      return assignedImage;
    }

    // Ensure unique images for each post
    const ensureUniqueImage = (imageUrl: string): boolean => {
      if (!fetchedImageUrls.has(imageUrl)) {
        fetchedImageUrls.add(imageUrl);
        return true;
      }
      return false;
    };

    // Attempt to fetch a unique image from Unsplash
    const fetchUniqueImageFromUnsplash = async (searchTerms: string): Promise<string | null> => {
      try {
        const response = await fetch(`https://source.unsplash.com/900x600/?${encodeURIComponent(searchTerms)}`);
        if (response.ok) {
          const imageUrl = response.url;
          if (ensureUniqueImage(imageUrl)) {
            return imageUrl;
          }
        }
      } catch (error) {
        console.error('Error fetching unique image from Unsplash:', error);
      }
      return null;
    };

    // Update the logic to use unique Unsplash images
    const searchTerms = post.tags && post.tags.length > 0 ? post.tags.join(',') : post.title.split(' ').slice(0, 3).join(',');
    const uniqueImage = await fetchUniqueImageFromUnsplash(searchTerms);
    if (uniqueImage) {
      imageAssignmentMap.set(post.slug, uniqueImage);
      console.log(`Assigned unique Unsplash image for "${post.title}": ${uniqueImage}`);
      return uniqueImage;
    }

    // Fallback if no unique image is found
    const fallbackImage = DEFAULT_IMAGES[Math.floor(Math.random() * DEFAULT_IMAGES.length)];
    imageAssignmentMap.set(post.slug, fallbackImage);
    console.log(`Assigned fallback image for "${post.title}": ${fallbackImage}`);
    return fallbackImage;
  } catch (error) {
    console.error('Error generating relevant image:', error);
    // Fallback to a default image
    return '/images/blog-fallback-1.jpg';
  }
}

// Function to ensure all blog posts have relevant images
export async function ensureRelevantImages(posts: BlogPostMetadata[]): Promise<BlogPostMetadata[]> {
  const updatedPosts = [...posts];
  
  // Reset the image assignment map when processing all posts together
  imageAssignmentMap.clear();
  fetchedImageUrls.clear();
  
  for (let i = 0; i < updatedPosts.length; i++) {
    // Check if the post needs an image
    if (!updatedPosts[i].image || 
        updatedPosts[i].image === '/images/default-blog-image.jpg' || 
        updatedPosts[i].image.includes('new blog post.svg') ||
        updatedPosts[i].image === '') {
      console.log(`Generating image for post: ${updatedPosts[i].title}`);
      try {
        updatedPosts[i].image = await generateRelevantImage(updatedPosts[i]);
        console.log(`Generated image: ${updatedPosts[i].image}`);
      } catch (error) {
        console.error(`Failed to generate image for ${updatedPosts[i].title}:`, error);
        // Use a random default image if generation fails
        const randomIndex = Math.floor(Math.random() * DEFAULT_IMAGES.length);
        updatedPosts[i].image = DEFAULT_IMAGES[randomIndex];
      }
    }
  }
  
  return updatedPosts;
}
